import { ChevronDown } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { type SkimLevel, useUISettingsStore } from '@/store/useUISettingsStore'

import { collectKeySentences } from './book-ai-position'
import { SKIM_LEVEL_LABELS } from './skim-highlight'
import { KeyPointList, SkimLevelBadge, SkimLevelSegment } from './skim-level-ui'
import type { BookAiReaderState } from './useBookAi'

type Props = {
  bookAi: BookAiReaderState
  onNavigate: (key: number) => void
}

/**
 * 重要箇所の一覧（AI が選んだ重要文を章ごとに並べ、タップでその箇所へ移動する）
 *
 * 小説は先の章の重要文がネタバレになるので、既定では今読んでいる章までにする。
 */
export const KeyPointsTab = ({ bookAi, onNavigate }: Props) => {
  const initialLevel = useUISettingsStore((state) => state.readerSkimLevel)
  const [level, setLevel] = useState<SkimLevel>(initialLevel)
  const isFiction = bookAi.status?.thesis?.genre === 'fiction'
  const [showAll, setShowAll] = useState(!isFiction)
  const currentIndex = bookAi.currentSectionIndex
  const currentRef = useRef<HTMLElement>(null)

  const visibleSections = useMemo(
    () => bookAi.sections.filter((s) => showAll || s.index <= Math.max(currentIndex, 0)),
    [bookAi.sections, showAll, currentIndex],
  )
  const groups = useMemo(
    () =>
      visibleSections
        .map((section) => ({ section, points: collectKeySentences([section], level) }))
        .filter((group) => group.points.length > 0),
    [visibleSections, level],
  )
  const counts = useMemo(() => {
    const all = collectKeySentences(visibleSections, 3)
    return ([1, 2, 3] as SkimLevel[]).map((l) => ({ level: l, count: all.filter((p) => p.level === l).length }))
  }, [visibleSections])
  const hiddenCount = bookAi.sections.length - visibleSections.length

  // 開いたときは今読んでいる章の位置から見せる
  useEffect(() => {
    currentRef.current?.scrollIntoView?.({ block: 'start' })
  }, [])

  return (
    <div className="space-y-3">
      <SkimLevelSegment value={level} onChange={setLevel} />
      <div className="flex gap-3 text-xs text-muted-foreground">
        {counts.map(({ level: l, count }) => (
          <span key={l} className="flex items-center gap-1">
            <SkimLevelBadge level={l} />
            {SKIM_LEVEL_LABELS[l]} {count}
          </span>
        ))}
      </div>

      {groups.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">この範囲に重要箇所がありません</p>
      ) : (
        groups.map(({ section, points }) => {
          const isCurrent = section.index === currentIndex
          return (
            <section key={section.index} ref={isCurrent ? currentRef : undefined} className="space-y-1.5">
              <h3 className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <span className="truncate">{section.title}</span>
                {isCurrent && <span className="shrink-0 text-primary">今読んでいる章</span>}
              </h3>
              <KeyPointList points={points} onNavigate={onNavigate} />
            </section>
          )
        })
      )}

      {hiddenCount > 0 && (
        <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAll(true)}>
          <ChevronDown className="size-4" />
          先の章も表示（{hiddenCount} 章・ネタバレに注意）
        </Button>
      )}
    </div>
  )
}
